/* eslint-disable react/no-unused-prop-types */

import { memo } from 'react';
import { GalleryCompare } from '~/app/compositions/content/gallery/gallery-compare';
import { useGalleryAltText } from '~/app/compositions/content/gallery/hooks/use-gallery-alt-text';
import {
	GalleryItem2Style,
	GalleryItem3Style,
	GalleryItemDefaultStyle,
	SpacingContainerStyles,
} from '~/app/compositions/content/gallery/styles/gallery-style';
import { useI18n } from '~/app/i18n/i18n';
import { BREAKPOINT_GALLERY_IMAGE_SIZES } from '~/app/models/constans';
import { ExtendedCatalogImage } from '~/graphql/hooks/use-images';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';

interface GalleryItemProps {
	media: Array<ExtendedCatalogImage>;
	currentMedia: Array<Array<ExtendedCatalogImage>>;
	className?: string;
	aspectRatio?: string;
	enableComparison?: boolean;
	index?: number;
	trackComparisonInteraction: () => void;
	activeIndex?: number;
	id: string;
}

export const GalleryItem = ({ ...props }: GalleryItemProps) => {
	switch (props?.media?.length) {
		case 2:
			return <GalleryItem2 {...props} />;
		case 3:
			return <GalleryItem3 {...props} />;
		default:
			return <GalleryItemDefault {...props} />;
	}
};

export const GalleryItemDefault = ({
	className,
	media,
	currentMedia,
	aspectRatio,
	enableComparison,
	index,
	trackComparisonInteraction,
	activeIndex,
	id,
}: GalleryItemProps) => {
	return media && Array.isArray(media) ? (
		<GalleryItemDefaultStyle className={className} $aspectRatio={aspectRatio}>
			<GalleryCompare
				currentMedia={currentMedia}
				aspectRatio={aspectRatio}
				enableComparison={enableComparison}
				index={index}
				trackComparisonInteraction={trackComparisonInteraction}
				activeIndex={activeIndex}
				id={id}>
				<VisualItem item={Array.isArray(media[0]) ? media[0][0] : media[0]} size={'large'} />
			</GalleryCompare>
		</GalleryItemDefaultStyle>
	) : null;
};

export const GalleryItem2 = ({
	className,
	media,
	currentMedia,
	enableComparison,
	index,
	trackComparisonInteraction,
	activeIndex,
	id,
}: GalleryItemProps) => {
	return media && Array.isArray(media) ? (
		<GalleryItem2Style className={className}>
			<div className={'left-side'}>
				<SpacingContainerStyles $placed={'right'} $aspectRatio={'4 / 3'}>
					<GalleryCompare
						currentMedia={currentMedia}
						aspectRatio={'4 / 3'}
						enableComparison={enableComparison}
						index={index}
						trackComparisonInteraction={trackComparisonInteraction}
						activeIndex={activeIndex}
						id={id}>
						<VisualItem item={media[0]} size={'large'} />
					</GalleryCompare>
				</SpacingContainerStyles>
			</div>
			<div className={'right-side'}>
				<SpacingContainerStyles $placed={'left'} $aspectRatio={'2 / 3'}>
					<VisualItem item={media[1]} size={'small'} />
				</SpacingContainerStyles>
			</div>
		</GalleryItem2Style>
	) : null;
};

export const GalleryItem3 = ({ className, media }: GalleryItemProps) => {
	return media && Array.isArray(media) ? (
		<GalleryItem3Style className={className}>
			<div className={'left-side'}>
				<SpacingContainerStyles $placed={'right'} $aspectRatio={'4 / 3'}>
					<VisualItem item={media[0]} size={'large'} />
				</SpacingContainerStyles>
			</div>
			<div className={'right-side'}>
				<SpacingContainerStyles $placed={'left'} $aspectRatio={' 2 / 3'} $flex={'unset'}>
					<SpacingContainerStyles $placed={'bottom'} $aspectRatio={' 4 / 3'}>
						<VisualItem item={media[1]} size={'small'} />
					</SpacingContainerStyles>
					<SpacingContainerStyles $placed={'top'} $aspectRatio={' 4 / 3'}>
						<VisualItem item={media[2]} size={'small'} />
					</SpacingContainerStyles>
				</SpacingContainerStyles>
			</div>
		</GalleryItem3Style>
	) : null;
};

// Returns a video if a video file (mimeType) is detected. Otherwise an image is returned.
export const VisualItem = ({ item, size }: { item: ExtendedCatalogImage; size: 'small' | 'large' }) => {
	const { i18n } = useI18n();
	const altText = useGalleryAltText(item);

	if (!item || !item.url) return <></>;

	if (item.mimeType && item.url) {
		if (item.mimeType.startsWith('video')) {
			return (
				<video autoPlay playsInline muted loop disablePictureInPicture aria-label={altText}>
					<source src={item.url} />
					<p>{i18n('ngc.errors.videonotsupported')}</p>
				</video>
			);
		}
		if (item.mimeType.startsWith('image/svg+xml')) {
			return <img src={item.url} alt={i18n('ngc.a11y.tiles.notavailable')} />; // <ResponsiveGalleryImage url={item.url} size={size} />;
		}

		return <ResponsiveGalleryImage url={item.url ?? ''} size={size} altText={altText} />;
	}

	// Fallback if MimeType is empty
	if (item.url?.endsWith('.mp4') || item.url?.endsWith('.webm')) {
		return (
			<video autoPlay playsInline muted loop disablePictureInPicture aria-label={altText}>
				<source src={item.url} />
				<p>{i18n('ngc.errors.videonotsupported')}</p>
			</video>
		);
	}

	return <ResponsiveGalleryImage url={item.url ?? ''} size={size} altText={altText} />;
};

const ResponsiveGalleryImage = memo(
	({ url, size, altText }: { url: string; size: 'small' | 'large'; altText: string }) => {
		// The image is loaded in different resolutions depending on the screen size

		return (
			<picture>
				{/* Small devices */}
				<source
					srcSet={modifyImageResolution(url || '', `${BREAKPOINT_GALLERY_IMAGE_SIZES[size].sm}`)}
					media="(max-width: 640px)"
				/>
				{/* Large devices */}
				<source
					srcSet={modifyImageResolution(url || '', `${BREAKPOINT_GALLERY_IMAGE_SIZES[size].md}`)}
					media="(max-width: 1920px)"
				/>
				{/* Extra Large devices (monitor displays) */}
				<img
					src={modifyImageResolution(url || '', `${BREAKPOINT_GALLERY_IMAGE_SIZES[size].lg}`)}
					alt={altText}
					loading="lazy"
				/>
			</picture>
		);
	},
);

ResponsiveGalleryImage.displayName = 'ResponsiveGalleryImage';
